/**
 * Sistema de cache para persistir datos en localStorage
 * Permite que la app funcione offline y mantenga datos entre navegaciones
 */

interface CacheEntry<T> {
  data: T
  timestamp: number
  expiresAt: number
}

const CACHE_PREFIX = 'colmafacil_cache_'
const DEFAULT_TTL = 60 * 60 * 2000 // 2 horas en milisegundos

/**
 * Guarda datos en cache con TTL (Time To Live)
 */
export function setCache<T>(key: string, data: T, ttl: number = DEFAULT_TTL): void {
  try {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl
    }
    localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(entry))
  } catch (error) {
    console.warn('Error saving to cache:', error)
    // Si localStorage está lleno, limpiar entradas antiguas
    clearExpiredCache()
  }
}

/**
 * Obtiene datos del cache si no han expirado
 */
export function getCache<T>(key: string): T | null {
  try {
    const cached = localStorage.getItem(`${CACHE_PREFIX}${key}`)
    if (!cached) return null

    const entry: CacheEntry<T> = JSON.parse(cached)
    
    // Verificar si ha expirado
    if (Date.now() > entry.expiresAt) {
      localStorage.removeItem(`${CACHE_PREFIX}${key}`)
      return null
    }

    return entry.data
  } catch (error) {
    console.warn('Error reading from cache:', error)
    return null
  }
}

/**
 * Obtiene datos del cache incluso si han expirado (útil para modo offline)
 */
export function getCacheStale<T>(key: string): T | null {
  try {
    const cached = localStorage.getItem(`${CACHE_PREFIX}${key}`)
    if (!cached) return null

    const entry: CacheEntry<T> = JSON.parse(cached)
    return entry.data
  } catch (error) {
    console.warn('Error reading stale cache:', error)
    return null
  }
}

/**
 * Obtiene la edad del cache en milisegundos
 */
export function getCacheAge(key: string): number | null {
  try {
    const cached = localStorage.getItem(`${CACHE_PREFIX}${key}`)
    if (!cached) return null

    const entry: CacheEntry<any> = JSON.parse(cached)
    return Date.now() - entry.timestamp
  } catch (error) {
    return null
  }
}

/**
 * Verifica si el cache es válido (no expirado y reciente)
 */
export function isCacheValid(key: string, maxAge: number): boolean {
  try {
    const cached = localStorage.getItem(`${CACHE_PREFIX}${key}`)
    if (!cached) return false

    const entry: CacheEntry<any> = JSON.parse(cached)
    const age = Date.now() - entry.timestamp
    return age < maxAge && Date.now() <= entry.expiresAt
  } catch (error) {
    return false
  }
}

/**
 * Elimina una entrada específica del cache
 */
export function removeCache(key: string): void {
  try {
    localStorage.removeItem(`${CACHE_PREFIX}${key}`)
  } catch (error) {
    console.warn('Error removing from cache:', error)
  }
}

/**
 * Elimina todas las entradas del cache que coincidan con un patrón
 * Útil para invalidar caches relacionados (ej: todas las stores)
 */
export function removeCacheByPattern(pattern: string): void {
  try {
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.startsWith(`${CACHE_PREFIX}${pattern}`)) {
        localStorage.removeItem(key)
      }
    })
  } catch (error) {
    console.warn('Error removing cache by pattern:', error)
  }
}

/**
 * Limpia todas las entradas expiradas del cache
 */
export function clearExpiredCache(): void {
  try {
    const keys = Object.keys(localStorage)
    const now = Date.now()
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        try {
          const cached = localStorage.getItem(key)
          if (cached) {
            const entry: CacheEntry<any> = JSON.parse(cached)
            if (now > entry.expiresAt) {
              localStorage.removeItem(key)
            }
          }
        } catch {
          // Si hay error parseando, eliminar la entrada corrupta
          localStorage.removeItem(key)
        }
      }
    })
  } catch (error) {
    console.warn('Error clearing expired cache:', error)
  }
}

/**
 * Limpia todo el cache
 */
export function clearAllCache(): void {
  try {
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key)
      }
    })
  } catch (error) {
    console.warn('Error clearing all cache:', error)
  }
}

/**
 * Verifica si hay conexión a internet
 */
export function isOnline(): boolean {
  return navigator.onLine
}

/**
 * Obtiene el tamaño aproximado del cache en bytes
 */
export function getCacheSize(): number {
  try {
    const keys = Object.keys(localStorage)
    let size = 0
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        const value = localStorage.getItem(key)
        if (value) {
          size += key.length + value.length
        }
      }
    })
    
    return size
  } catch {
    return 0
  }
}

/**
 * Limpia el cache si excede un tamaño máximo (útil para prevenir llenar localStorage)
 */
export function clearCacheIfNeeded(maxSize: number = 5 * 1024 * 1024): void {
  const currentSize = getCacheSize()
  if (currentSize > maxSize) {
    // Eliminar las entradas más antiguas primero
    try {
      const keys = Object.keys(localStorage)
      const entries: Array<{ key: string; timestamp: number }> = []
      
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          try {
            const cached = localStorage.getItem(key)
            if (cached) {
              const entry: CacheEntry<any> = JSON.parse(cached)
              entries.push({ key, timestamp: entry.timestamp })
            }
          } catch {
            // Ignorar entradas corruptas
          }
        }
      })
      
      // Ordenar por timestamp (más antiguas primero)
      entries.sort((a, b) => a.timestamp - b.timestamp)
      
      // Eliminar el 50% más antiguo
      const toRemove = Math.floor(entries.length / 2)
      for (let i = 0; i < toRemove; i++) {
        localStorage.removeItem(entries[i].key)
      }
    } catch (error) {
      console.warn('Error clearing cache by size:', error)
    }
  }
}

/**
 * Obtiene estadísticas del cache
 */
export function getCacheStats() {
  try {
    const keys = Object.keys(localStorage)
    const cacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX))
    const now = Date.now()
    
    let totalSize = 0
    let validEntries = 0
    let expiredEntries = 0
    
    cacheKeys.forEach(key => {
      try {
        const cached = localStorage.getItem(key)
        if (cached) {
          totalSize += cached.length
          const entry: CacheEntry<any> = JSON.parse(cached)
          
          if (now > entry.expiresAt) {
            expiredEntries++
          } else {
            validEntries++
          }
        }
      } catch (error) {
        // Ignorar entradas corruptas
      }
    })
    
    // Calcular tamaño en KB/MB
    const sizeKB = Math.round(totalSize / 1024)
    const sizeMB = Math.round((totalSize / 1024 / 1024) * 100) / 100
    
    // Estimar límite de localStorage (típicamente 5-10MB)
    const estimatedLimit = 5 * 1024 * 1024 // 5MB
    const usagePercent = Math.round((totalSize / estimatedLimit) * 100)
    
    return {
      totalEntries: cacheKeys.length,
      validEntries,
      expiredEntries,
      sizeBytes: totalSize,
      sizeKB,
      sizeMB,
      usagePercent: Math.min(usagePercent, 100),
      estimatedLimitMB: 5
    }
  } catch (error) {
    console.warn('Error getting cache stats:', error)
    return {
      totalEntries: 0,
      validEntries: 0,
      expiredEntries: 0,
      sizeBytes: 0,
      sizeKB: 0,
      sizeMB: 0,
      usagePercent: 0,
      estimatedLimitMB: 5
    }
  }
}

